import React, { useState, useEffect, useCallback } from 'react';
import { useForm } from 'react-hook-form';
import toast from 'react-hot-toast';
import { Plus, Pencil, Trash2, X, Check, Shield } from 'lucide-react';
import api from '../../api/client';
import ConfirmDialog from '../../components/common/ConfirmDialog';

const MODULES = ['masters','tutorials','chapters','schools','roles','creator_videos','blog_videos','users','roles','reports','batches','orders','direct_codes','school_users'];
const ACTIONS = ['view','create','edit','delete'];

const DEFAULT_PERMS = () =>
  Object.fromEntries(MODULES.map(m => [m, Object.fromEntries(ACTIONS.map(a => [a, 0]))]));

export default function RolesPage() {
  const [roles, setRoles]     = useState([]);
  const [loading, setLoading] = useState(true);
  const [modal, setModal]     = useState(false);
  const [editing, setEditing] = useState(null);
  const [confirm, setConfirm] = useState(null);
  const [perms, setPerms]     = useState(DEFAULT_PERMS());

  const { register, handleSubmit, reset, formState: { errors } } = useForm();

  const load = useCallback(async () => {
    try { const { data } = await api.get('/roles'); setRoles(data.data); }
    catch { toast.error('Failed to load roles'); } finally { setLoading(false); }
  }, []);

  useEffect(() => { load(); }, [load]);

  const openCreate = () => {
    setEditing(null);
    setPerms(DEFAULT_PERMS());
    reset({ role_name: '', display_name: '', is_active: true });
    setModal(true);
  };

  const openEdit = (role) => {
    setEditing(role);
    const p = typeof role.permissions === 'string' ? JSON.parse(role.permissions) : role.permissions;
    // Merge with defaults so all modules present
    const merged = DEFAULT_PERMS();
    Object.keys(p).forEach(m => { if (merged[m]) Object.assign(merged[m], p[m]); });
    setPerms(merged);
    reset({ display_name: role.display_name, is_active: !!role.is_active });
    setModal(true);
  };

  const togglePerm = (module, action) => {
    setPerms(prev => ({
      ...prev,
      [module]: { ...prev[module], [action]: prev[module][action] ? 0 : 1 }
    }));
  };

  const onSubmit = async (values) => {
    try {
      const payload = { ...values, permissions: perms };
      if (editing) {
        await api.put(`/roles/${editing.id}`, payload);
        toast.success('Role updated');
      } else {
        await api.post('/roles', { ...payload, role_name: values.role_name });
        toast.success('Role created');
      }
      setModal(false); load();
    } catch (err) { toast.error(err.response?.data?.message || 'Error'); }
  };

  const doDelete = async (id) => {
    try { await api.delete(`/roles/${id}`); toast.success('Deleted'); setConfirm(null); load(); }
    catch (err) { toast.error(err.response?.data?.message || 'Error'); setConfirm(null); }
  };

  return (
    <div>
      <div className="page-header">
        <div>
          <h1 className="page-title">Roles &amp; Permissions</h1>
          <p className="page-subtitle">Define what each role can access and do</p>
        </div>
        <button className="btn btn-primary" onClick={openCreate}><Plus size={15}/> Add Role</button>
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill,minmax(320px,1fr))', gap: 18 }}>
        {loading ? <div className="loader-wrap"><div className="loader"/></div> : roles.map(role => {
          const p = typeof role.permissions === 'string' ? JSON.parse(role.permissions) : role.permissions;
          const activeModules = Object.entries(p).filter(([,v]) => Object.values(v).some(Boolean));
          return (
            <div className="card" key={role.id}>
              <div className="card-header">
                <span className="card-title">
                  <Shield size={16} style={{ color: 'var(--orange-400)' }} />
                  {role.display_name}
                </span>
                <div style={{ display: 'flex', gap: 6, alignItems: 'center' }}>
                  {role.is_active
                    ? <span className="badge badge-green">Active</span>
                    : <span className="badge badge-grey">Inactive</span>}
                  {role.role_name !== 'super_admin' && (
                    <>
                      <button className="btn-icon" onClick={() => openEdit(role)}><Pencil size={14}/></button>
                      <button className="btn-icon" onClick={() => setConfirm(role.id)}><Trash2 size={14}/></button>
                    </>
                  )}
                </div>
              </div>
              <div className="card-body" style={{ paddingTop: 12 }}>
                <code style={{ fontSize: 11, color: 'var(--grey-400)' }}>{role.role_name}</code>
                <div style={{ marginTop: 12, display: 'flex', flexWrap: 'wrap', gap: 6 }}>
                  {activeModules.map(([mod, acts]) => {
                    const activeActs = Object.entries(acts).filter(([,v]) => v).map(([k]) => k);
                    return (
                      <div key={mod} style={{
                        background: 'var(--orange-50)', border: '1px solid var(--orange-100)',
                        borderRadius: 6, padding: '5px 10px', fontSize: 11,
                      }}>
                        <strong style={{ color: 'var(--orange-700)', textTransform: 'capitalize' }}>
                          {mod.replace('_',' ')}
                        </strong>
                        <span style={{ color: 'var(--grey-400)', marginLeft: 4 }}>
                          {activeActs.join('·')}
                        </span>
                      </div>
                    );
                  })}
                  {!activeModules.length && <span style={{ fontSize: 12, color: 'var(--grey-400)' }}>No permissions</span>}
                </div>
              </div>
            </div>
          );
        })}
      </div>

      {/* Modal */}
      {modal && (
        <div className="modal-overlay" onClick={() => setModal(false)}>
          <div className="modal modal-lg" onClick={e => e.stopPropagation()}>
            <div className="modal-header">
              <span className="modal-title">{editing ? 'Edit' : 'Create'} Role</span>
              <button className="btn-icon" onClick={() => setModal(false)}><X size={18}/></button>
            </div>
            <form onSubmit={handleSubmit(onSubmit)}>
              <div className="modal-body">
                <div className="form-grid">
                  {!editing && (
                    <div className="form-group">
                      <label className="form-label">Role Slug <span className="req">*</span></label>
                      <input className={`form-control ${errors.role_name?'error':''}`} placeholder="e.g. sales_manager"
                        {...register('role_name',{required:'Required'})} />
                      <p style={{fontSize:11,color:'var(--grey-400)',marginTop:3}}>Lowercase, no spaces (use _ )</p>
                    </div>
                  )}
                  <div className="form-group">
                    <label className="form-label">Display Name <span className="req">*</span></label>
                    <input className={`form-control ${errors.display_name?'error':''}`} placeholder="Sales Manager"
                      {...register('display_name',{required:'Required'})} />
                  </div>
                </div>

                <div style={{ marginBottom: 18 }}>
                  <label className="toggle">
                    <input type="checkbox" {...register('is_active')} />
                    <span className="toggle-track"><span className="toggle-thumb"/></span>
                    <span style={{fontSize:13}}>Active</span>
                  </label>
                </div>

                {/* Permission matrix */}
                <div style={{ border: '1px solid var(--orange-200)', borderRadius: 8, overflow: 'hidden' }}>
                  <div className="permission-grid">
                    <div className="perm-header" style={{ textAlign: 'left', paddingLeft: 16 }}>Module</div>
                    {ACTIONS.map(a => <div key={a} className="perm-header">{a}</div>)}
                  </div>
                  {MODULES.map(mod => (
                    <div key={mod} className="permission-grid perm-row">
                      <div className="perm-module">{mod.replace('_',' ')}</div>
                      {ACTIONS.map(action => (
                        <div key={action} className="perm-cell">
                          <label className="toggle">
                            <input
                              type="checkbox"
                              checked={!!perms[mod]?.[action]}
                              onChange={() => togglePerm(mod, action)}
                            />
                            <span className="toggle-track" style={{width:32,height:18}}>
                              <span className="toggle-thumb" style={{width:12,height:12,top:3,left:3}}
                                data-checked={perms[mod]?.[action] ? 'true' : 'false'}
                              />
                            </span>
                          </label>
                        </div>
                      ))}
                    </div>
                  ))}
                </div>
              </div>
              <div className="modal-footer">
                <button type="button" className="btn btn-secondary" onClick={() => setModal(false)}>Cancel</button>
                <button type="submit" className="btn btn-primary"><Check size={14}/> Save Role</button>
              </div>
            </form>
          </div>
        </div>
      )}

      <ConfirmDialog open={!!confirm} title="Delete Role"
        message="Ensure no users are assigned this role before deleting."
        onConfirm={() => doDelete(confirm)} onCancel={() => setConfirm(null)} />
    </div>
  );
}
